'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Modal } from '../Modal';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs';
import Dropdown from '@/components/forms/controls/Dropdown';
import { DatePicker } from '@/components/forms/controls/DatePicker';
import Button from '@/components/ui-components/Button';
import { theme } from '@/config/theme';
import { Input } from '@/components/forms/controls/Input';
import { CloseIconRed, CloseIconModal } from '@/components/icons';
import { DropdownOption } from '@/types/components';

/**
 * Dynamic dropdown filter configuration
 */
export interface DynamicFilter {
  /** Unique identifier for the filter */
  id: string;
  /** Label displayed above the dropdown */
  label: string;
  /** Placeholder text when no option is selected */
  placeholder: string;
  /** Available options for the dropdown (required for dropdown type) */
  options?: DropdownOption[];
  /** Enable search functionality for this dropdown */
  searchable?: boolean;
  /** Filter type: 'dropdown', 'number', or 'text' */
  type?: 'dropdown' | 'number' | 'text';
  /** Minimum value for number input */
  min?: number;
  /** Maximum value for number input */
  max?: number;
  /** Condition function to determine if filter should be shown */
  condition?: (filters: FilterValues) => boolean;
}

/**
 * Filter configuration interface
 * Allows customization of filters with dynamic dropdowns
 */
export interface FilterConfig {
  /** Array of dynamic dropdown filters */
  filters?: DynamicFilter[];
  /** Always show date range filter (permanent) */
  showDateRange?: boolean;
}

/**
 * Filter values interface - now dynamic
 * Keys correspond to filter IDs from config, plus dateFrom/dateTo
 */
export interface FilterValues {
  [key: string]: string | undefined;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * FilterModal component props
 */
export interface FilterModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when filters are applied */
  onApply: (filters: FilterValues) => void;
  /** Filter configuration */
  config: FilterConfig;
  /** Initial filter values */
  initialValues?: FilterValues;
  /** Modal title */
  title?: string;
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 24px;
  border-bottom: 1px solid ${theme.colors.border.primary};
  margin-bottom: 24px;
`;

const CloseIconButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${theme.colors.status.errorAlt};
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    opacity: 0.7;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 24px;
  position: relative;
  /* Allow dropdowns to overflow the grid */
  overflow: visible;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  grid-column: 1 / -1;
`;

const DateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DateLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.gray[900]};
  margin-bottom: 2px;
`;

const FilterFieldWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-end;
  /* Remove z-index to avoid creating stacking contexts that interfere with dropdowns */
  /* Ensure dropdown container is relative for absolute positioning */
  > div {
    position: relative;
  }
  /* Ensure dropdown menu can overflow and appear above other inputs */
  [role="listbox"] {
    z-index: 1003 !important; /* Higher than modal z-index (1000) and other inputs */
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 36px;
  bottom: 9px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[500]};
  transition: all 0.2s;
  z-index: 10;
  height: 18px;
  width: 18px;
  &:hover {
    color: ${theme.colors.gray[700]};
    background-color: ${theme.colors.gray[100]};
    border-radius: 3px;
  }
  &:focus {
    outline: none;
    color: ${theme.colors.gray[700]};
  }
  &:active {
    background-color: ${theme.colors.gray[200]};
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;

const DateFieldClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 28px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[500]};
  transition: color 0.2s;
  z-index: 1;
  &:hover {
    color: ${theme.colors.gray[700]};
  }
  &:focus {
    outline: none;
    color: ${theme.colors.gray[700]};
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${theme.colors.gray[200]};
`;

/**
 * FilterModal Component
 * 
 * A highly configurable modal for filtering data with dynamic dropdown filters and date range.
 * Instead of fixed filter types, you can define any number of custom dropdown filters.
 * Date range filter is always available.
 * 
 * @component
 * @example
 * ```tsx
 * const config: FilterConfig = {
 *   filters: [
 *     {
 *       id: 'category',
 *       label: 'Category',
 *       placeholder: 'Select category',
 *       options: [
 *         { label: 'Software', value: '1' },
 *         { label: 'Hardware', value: '2' }
 *       ],
 *       searchable: true
 *     },
 *     {
 *       id: 'status',
 *       label: 'Status',
 *       placeholder: 'Select status',
 *       options: [
 *         { label: 'Active', value: 'active' },
 *         { label: 'Inactive', value: 'inactive' }
 *       ]
 *     }
 *   ],
 *   showDateRange: true
 * };
 * 
 * <FilterModal
 *   isOpen={showFilters}
 *   onClose={() => setShowFilters(false)}
 *   onApply={(filters) => handleApplyFilters(filters)}
 *   config={config}
 * />
 * ```
 */
export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  config,
  initialValues = {},
  title = 'Filter',
}) => {
  const [filters, setFilters] = useState<FilterValues>(initialValues);
  const isFirstRender = useRef(true);

  // Reset filters when modal opens - using ref to avoid infinite loop
  useEffect(() => {
    if (isOpen && !isFirstRender.current) {
      setFilters(initialValues);
    }
    isFirstRender.current = false;
  }, [isOpen, initialValues]);

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [key]: value,
      };
      
      // Validate number inputs against min/max constraints
      const filterConfig = config.filters?.find(f => f.id === key);
      if (filterConfig && (filterConfig.type === 'number' || filterConfig.type === 'text')) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Enforce max constraint
          if (filterConfig.max !== undefined && numValue > filterConfig.max) {
            updated[key] = filterConfig.max.toString();
          }
          // Enforce min constraint
          if (filterConfig.min !== undefined && numValue < filterConfig.min) {
            updated[key] = filterConfig.min.toString();
          }
        }
      }
      
      // If origin filter changes, check if status_code filter should be cleared
      if (key === 'origin') {
        const statusCodeFilter = config.filters?.find(f => f.id === 'status_code');
        if (statusCodeFilter && statusCodeFilter.condition) {
          // If condition is no longer met, clear status_code filter
          if (!statusCodeFilter.condition(updated)) {
            delete updated.status_code;
          }
        }
      }
      
      return updated;
    });
  };

  const handleClearAll = () => {
    // Clear all filters, preselecting "all" option where available
    const clearedFilters: FilterValues = {};
    
    // For each filter in config, set to "all" if available, otherwise leave undefined
    if (config.filters) {
      config.filters.forEach(filter => {
        if (filter.options) {
          // Check if "all" option exists
          const hasAllOption = filter.options.some(opt => opt.value === 'all');
          if (hasAllOption) {
            // Preselect "all" option
            clearedFilters[filter.id] = 'all';
          }
          // If no "all" option, don't set anything (filter will be cleared)
        }
      });
    }
    
    // For date range, clear them (no "all" option for dates)
    // Don't set dateFrom/dateTo in clearedFilters, they'll be undefined/cleared
    
    setFilters(clearedFilters);
  };

  const handleClearFilter = (key: keyof FilterValues) => {
    setFilters(prev => {
      const updated = { ...prev };
      
      // Check if this filter has an "all" option
      const filterConfig = config.filters?.find(f => f.id === key);
      if (filterConfig && filterConfig.options) {
        // Check if "all" option exists
        const hasAllOption = filterConfig.options.some(opt => opt.value === 'all');
        if (hasAllOption) {
          // Preselect "all" option instead of deleting
          updated[key] = 'all';
        } else {
          // No "all" option, delete the filter
          delete updated[key];
        }
      } else {
        // Not a dropdown filter or no options, just delete
        delete updated[key];
      }
      
      return updated;
    });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  // Check if filters are active (excluding "all" values and empty values)
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      // Exclude "all" values and empty/undefined values
      if (value === 'all' || value === undefined || value === '') {
        return false;
      }
      return true;
    });
  }, [filters]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="700px"
      maxHeight="85vh"
    >
      <ModalHeader>
        <Breadcrumbs
          items={[{ label: title }]}
          accentColor="#FB6F2A"
          style={{ padding: 0 }}
        />
        <CloseIconButton
          onClick={onClose}
          type="button"
          aria-label="Close filter modal"
          title="Close"
        >
          <CloseIconModal width={24} height={24} stroke="#D3072A" />
        </CloseIconButton>
      </ModalHeader>
      <FilterGrid>
        {/* Dynamic filters - dropdown or number input */}
        {config.filters && config.filters.map((filter) => {
          // Check if filter should be shown based on condition
          if (filter.condition && !filter.condition(filters)) {
            return null;
          }
          
          const filterType = filter.type || 'dropdown';
          const filterValue = filters[filter.id];
          // Show clear button only if value exists, is not empty, and is not "all" (default/inactive)
          const hasValue = filterValue !== undefined && filterValue !== '' && filterValue !== 'all';

          if (filterType === 'number' || filterType === 'text') {
            return (
              <FilterFieldWrapper key={filter.id}>
                <Input
                  label={filter.label}
                  type={filterType}
                  value={filterValue || ''}
                  onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                  placeholder={filter.placeholder}
                  fullWidth
                  {...(filterType === 'number' && filter.min !== undefined && { min: filter.min })}
                  {...(filterType === 'number' && filter.max !== undefined && { max: filter.max })}
                />
                {hasValue && (
                  <ClearButton
                    onClick={() => handleClearFilter(filter.id)}
                    type="button"
                    aria-label={`Clear ${filter.label}`}
                    title={`Clear ${filter.label}`}
                  >
                    <CloseIconRed width={14} height={14} />
                  </ClearButton>
                )}
              </FilterFieldWrapper>
            );
          }

          return (
            <FilterFieldWrapper key={filter.id}>
              <div style={{ position: 'relative' }}>
                <Dropdown
                  label={filter.label}
                  options={filter.options || []}
                  value={filterValue}
                  onChange={(value) => handleFilterChange(filter.id, value)}
                  placeholder={filter.placeholder}
                  searchable={filter.searchable}
                  variant="form"
                  useFixedPosition={true}
                />
                {hasValue && (
                  <ClearButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleClearFilter(filter.id);
                    }}
                    type="button"
                    aria-label={`Clear ${filter.label}`}
                    title={`Clear ${filter.label}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <CloseIconRed width={14} height={14} />
                  </ClearButton>
                )}
              </div>
            </FilterFieldWrapper>
          );
        })}

        {/* Date range filter - always visible if enabled */}
        {config.showDateRange && (
          <DateRangeContainer>
            <DateFieldWrapper>
              <DateLabel>Date From</DateLabel>
              <div style={{ position: 'relative' }}>
                <DatePicker
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  placeholder="Start date"
                  fullWidth
                  {...(filters.dateTo && { max: filters.dateTo })}
                />
                {filters.dateFrom && (
                  <DateFieldClearButton
                    onClick={() => handleClearFilter('dateFrom')}
                    type="button"
                    aria-label="Clear Date From"
                    title="Clear Date From"
                  >
                    <CloseIconRed width={16} height={16} />
                  </DateFieldClearButton>
                )}
              </div>
            </DateFieldWrapper>
            <DateFieldWrapper>
              <DateLabel>Date To</DateLabel>
              <div style={{ position: 'relative' }}>
                <DatePicker
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  placeholder="End date"
                  fullWidth
                  {...(filters.dateFrom && { min: filters.dateFrom })}
                />
                {filters.dateTo && (
                  <DateFieldClearButton
                    onClick={() => handleClearFilter('dateTo')}
                    type="button"
                    aria-label="Clear Date To"
                    title="Clear Date To"
                  >
                    <CloseIconRed width={16} height={16} />
                  </DateFieldClearButton>
                )}
              </div>
            </DateFieldWrapper>
          </DateRangeContainer>
        )}
      </FilterGrid>
      <ModalActions>
        <Button
          variant="outline"
          onClick={handleClearAll}
          disabled={!hasActiveFilters}
        >
          Clear All
        </Button>
        <Button
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
        >
          Apply Filters
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default FilterModal;

